import * as React from 'react'
import { useFocusable, SpatialNavigation } from '@noriginmedia/norigin-spatial-navigation'
import { isKeyboardModality } from '../focus'

export interface FocusableProps {
  /** The content this stop wraps — a tile, a nav link, a hero action… */
  children: React.ReactNode;
  /** Extra class(es) on the focusable wrapper. */
  className?: string;
}

/** What actually takes DOM focus inside a stop: the real interactive element. */
const INTERACTIVE = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])'

/**
 * One stop in the spatial-navigation tree — the primitive behind every
 * remote-navigable element (row cells, nav items, hero actions).
 *
 * The important part is that virtual and real focus are kept in sync BOTH ways:
 *
 *   remote/arrow → Norigin focuses this stop → we move real DOM focus onto the
 *                  inner link/button
 *   Tab/click    → the browser moves real DOM focus here → we tell Norigin, so
 *                  arrows carry on from where the viewer actually is
 *
 * Without that mirror the app has two independent cursors: Norigin's ring in one
 * place and the browser's Tab focus in another, with screen readers following
 * neither. Because DOM focus is the source of truth, the ring is driven by real
 * focus (`hasFocus`) rather than Norigin's virtual state — so tabbing away to
 * something outside the tree (the search box) correctly drops the highlight.
 */
export function Focusable ({ children, className }: FocusableProps) {
  const [hasFocus, setHasFocus] = React.useState(false)

  const { ref, focusKey } = useFocusable({
    // Activate the stop's real control on OK/Enter.
    //
    // This is NOT redundant with the element being a natively-activatable <a> or
    // <button>: Norigin's key listener calls preventDefault() + stopPropagation()
    // on every key in its keymap — Enter (13) included — so the browser never
    // performs the default activation. Without this, focus moves fine but Return
    // does nothing. For the same reason there is no double-fire: the native
    // activation we would collide with has already been suppressed.
    onEnterPress: () => {
      const el = ref.current as HTMLElement | null
      const target = (el?.querySelector(INTERACTIVE) ?? el) as HTMLElement | null
      target?.click()
    },
    onFocus: () => {
      const el = ref.current as HTMLElement | null
      if (!el) return
      // Mirror virtual focus onto the DOM. preventScroll: the browser's own
      // focus scrolling would fight the centring we do just below.
      const target = el.querySelector(INTERACTIVE) as HTMLElement | null
      if (target && document.activeElement !== target) target.focus({ preventScroll: true })
      // Only chase the focus for remote/keyboard viewers — a mouse click must not
      // make the row scroll away under the pointer.
      if (isKeyboardModality()) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
      }
    },
  })

  // React's onFocus/onBlur on a container are focusin/focusout: they fire for
  // descendants too, which is what lets us observe Tab landing on the inner link.
  const handleFocus = () => {
    setHasFocus(true)
    if (SpatialNavigation.getCurrentFocusKey() !== focusKey) {
      // Tab (or a click) got here first — realign the spatial tree. Guarded on the
      // key so the mirror above cannot loop back into itself.
      SpatialNavigation.setFocus(focusKey)
    }
  }

  return (
    <div
      ref={ref}
      data-focus-key={focusKey}
      data-focused={hasFocus || undefined}
      className={['mlsg-focusable', hasFocus ? 'mlsg-focusable--focused' : '', className].filter(Boolean).join(' ')}
      onFocus={handleFocus}
      onBlur={() => setHasFocus(false)}
    >
      {children}
    </div>
  )
}
